import { useEffect, useState } from "react";
import { conversationKey } from "../panel";
import { readPendingWork } from "./api";
import { NO_PENDING_WORK, continuable, destructiveInFlight, type PendingWork } from "./pending";

/** What the Continue control shows and does for the conversation on screen. */
export interface ContinueState {
  pending: PendingWork;
  /** The user clicked Continue on a run with a destructive tool in flight: asking before it runs again. */
  confirming: boolean;
  onContinue: () => void;
  dismiss: () => void;
}

/**
 * Whether the open workflow conversation has a cut-off run to continue, read from
 * the agent when the conversation opens and again whenever a turn ends — never
 * while one runs. Continuing is always the user's click (owner, 2026-10-10):
 * nothing here re-runs anything by itself. A destructive tool that was in flight
 * is named and confirmed first; the agent refuses the continue without that word.
 */
export function useContinueRun(input: {
  scope: { token: string | null; orgId: string | null; projectId: string | null; userId: string | null; threadId: string | null };
  /** The workflow on screen, by code; null outside a workflow's conversation. */
  workflowCode: string | null;
  busy: boolean;
  continueRun: (options?: { confirmDestructive?: boolean }) => void;
}): ContinueState {
  const { scope, workflowCode, busy } = input;
  const { token, orgId, projectId, userId, threadId } = scope;
  const key = conversationKey(scope);
  const [loaded, setLoaded] = useState<{ key: string; pending: PendingWork }>({ key: "", pending: NO_PENDING_WORK });
  // The conversation whose destructive continue is being confirmed, so a switch drops the question.
  const [confirmingKey, setConfirmingKey] = useState("");
  const ready = workflowCode && token && orgId && projectId && userId && threadId
    ? { token, orgId, projectId, userId, threadId, workflowCode } : null;

  useEffect(() => {
    if (!ready || busy) return;
    const controller = new AbortController();
    readPendingWork(ready, controller.signal)
      .then((pending) => setLoaded({ key, pending }))
      .catch(() => { if (!controller.signal.aborted) setLoaded({ key, pending: NO_PENDING_WORK }); });
    return () => controller.abort();
    // `ready` is rebuilt every render; `key`, `token` and `workflowCode` stand for it. `busy`
    // falling to false is what makes a just-ended turn re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, key, workflowCode, busy]);

  const pending = loaded.key === key && !busy ? loaded.pending : NO_PENDING_WORK;
  const confirming = confirmingKey === key && continuable(pending);

  return {
    pending,
    confirming,
    onContinue: () => {
      if (busy || !continuable(pending)) return;
      const destructive = destructiveInFlight(pending);
      if (destructive.length > 0 && !confirming) {
        setConfirmingKey(key);
        return;
      }
      setConfirmingKey("");
      // Nothing to offer while the continue runs; the read after it says what is left.
      setLoaded({ key, pending: NO_PENDING_WORK });
      input.continueRun({ confirmDestructive: destructive.length > 0 });
    },
    dismiss: () => setConfirmingKey(""),
  };
}
